/**
 * Phase 4 Task 12 — supervisor dashboard Zustand store.
 *
 * Holds the live agent grid, active-call list, and a transient stack of
 * supervisor alerts (network-quality, fraud, KPI breaches). The data
 * feed (Redis aggregation + AMQP fanout into the supervisor room) lands
 * in a follow-up; the store contract is stable and lets the UI render
 * empty until the feed is wired.
 */
import { create } from 'zustand'
import type { SupervisorAgentSummary, SupervisorLiveCall } from '@kc3/types'

export interface SupervisorAlert {
  id: number
  severity: 'info' | 'warn' | 'error'
  message: string
  at: string
}

interface SupervisorStoreState {
  agentList: SupervisorAgentSummary[]
  liveCalls: SupervisorLiveCall[]
  alerts: SupervisorAlert[]
  setAgentList: (agents: SupervisorAgentSummary[]) => void
  updateAgent: (agent: SupervisorAgentSummary) => void
  setLiveCalls: (calls: SupervisorLiveCall[]) => void
  addAlert: (alert: Omit<SupervisorAlert, 'id'>) => void
  clearAlert: (id: number) => void
  reset: () => void
}

let alertSeq = 0

const initialState = {
  agentList: [] as SupervisorAgentSummary[],
  liveCalls: [] as SupervisorLiveCall[],
  alerts: [] as SupervisorAlert[],
} satisfies Pick<SupervisorStoreState, 'agentList' | 'liveCalls' | 'alerts'>

export const useSupervisorStore = create<SupervisorStoreState>()((set) => ({
  ...initialState,
  setAgentList: (agents) => set({ agentList: agents }),
  updateAgent: (agent) =>
    set((state) => {
      const idx = state.agentList.findIndex((a) => a.id === agent.id)
      if (idx === -1) return { agentList: [...state.agentList, agent] }
      const next = state.agentList.slice()
      next[idx] = agent
      return { agentList: next }
    }),
  setLiveCalls: (calls) => set({ liveCalls: calls }),
  addAlert: (alert) =>
    set((state) => ({
      alerts: [...state.alerts, { ...alert, id: ++alertSeq }],
    })),
  clearAlert: (id) =>
    set((state) => ({ alerts: state.alerts.filter((a) => a.id !== id) })),
  reset: () => set({ ...initialState }),
}))
